import { Copy, Timer } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
	formatTimekeeperFullName,
	type Timekeeper,
} from "@/features/attorney/views/Entries/timekeepers";
import { AttachmentField } from "@/v2/components/entry-form/AttachmentField";
import {
	EntryForm,
	type EntryFormHandle,
} from "@/v2/components/entry-form/EntryForm";
import type { TimeEntryFormValues } from "@/v2/components/entry-form/schema";
import { Label } from "@/v2/components/ui/label";
import {
	ResponsiveDialog,
	ResponsiveDialogContent,
} from "@/v2/components/ui/responsive-dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/v2/components/ui/select";
import { TooltipButton } from "@/v2/components/ui/tooltip-button";
import { createTimerEntryDefaults } from "@/v2/lib/entryPayloads";
import { formatDurationClock } from "@/v2/lib/time";

export interface AddEntryDialogProps {
	open: boolean;
	date: Date;
	timerPrefillDuration: number | null;
	onOpenChange: (open: boolean) => void;
	onSubmit: (
		values: TimeEntryFormValues,
		files: File[],
		timekeeperId: string | null,
	) => void;
	onExport: (
		values: TimeEntryFormValues,
		files: File[],
		timekeeperId: string | null,
	) => void;
	onDuplicate: (
		values: TimeEntryFormValues,
		files: File[],
		timekeeperId: string | null,
	) => void;
	canUploadFiles: boolean;
	timekeepers: readonly Timekeeper[] | null;
	defaultTimekeeperId?: string | null;
}

export function AddEntryDialog({
	open,
	date,
	timerPrefillDuration,
	onOpenChange,
	onSubmit,
	onExport,
	onDuplicate,
	canUploadFiles,
	timekeepers,
	defaultTimekeeperId = null,
}: AddEntryDialogProps) {
	const formKey = `${date.toISOString()}-${timerPrefillDuration ?? "manual"}`;
	const formRef = useRef<EntryFormHandle>(null);
	const [files, setFiles] = useState<File[]>([]);
	const [selectedTimekeeperId, setSelectedTimekeeperId] = useState<
		string | null
	>(defaultTimekeeperId);
	useEffect(() => {
		if (open) setSelectedTimekeeperId(defaultTimekeeperId);
	}, [defaultTimekeeperId, open]);
	const handleOpenChange = (nextOpen: boolean) => {
		if (!nextOpen) setFiles([]);
		onOpenChange(nextOpen);
	};

	return (
		<ResponsiveDialog open={open} onOpenChange={handleOpenChange}>
			<ResponsiveDialogContent
				className="!max-h-[90vh]"
				title="Add Entry"
				headerActions={
					timerPrefillDuration !== null ? (
						<TimerElapsedBadge durationSeconds={timerPrefillDuration} />
					) : undefined
				}
				headerClassName="p-4 pb-2 pr-12"
				footer={
					<div className="flex items-center justify-between gap-3">
						<TooltipButton
							type="button"
							variant="outline"
							size="icon"
							aria-label="Duplicate entry"
							tooltip="Duplicate entry"
							onClick={() => formRef.current?.submitDuplicate()}
							disabled={Boolean(timekeepers?.length) && !selectedTimekeeperId}
						>
							<Copy />
						</TooltipButton>
						<div className="flex items-center gap-2">
							<TooltipButton
								type="button"
								variant="outline"
								size="sm"
								tooltip="Export"
								onClick={() => formRef.current?.submitExport()}
								disabled={Boolean(timekeepers?.length) && !selectedTimekeeperId}
							>
								Export
							</TooltipButton>
							<TooltipButton
								type="submit"
								form="add-entry-form"
								size="sm"
								tooltip="Add"
								disabled={Boolean(timekeepers?.length) && !selectedTimekeeperId}
							>
								Add
							</TooltipButton>
						</div>
					</div>
				}
			>
				{timekeepers && timekeepers.length > 1 && (
					<div className="grid gap-1.5 px-4 pt-4">
						<Label htmlFor="entry-timekeeper">Timekeeper</Label>
						<Select
							value={selectedTimekeeperId ?? undefined}
							onValueChange={setSelectedTimekeeperId}
						>
							<SelectTrigger id="entry-timekeeper">
								<SelectValue placeholder="Select timekeeper" />
							</SelectTrigger>
							<SelectContent>
								{timekeepers.map((timekeeper) => (
									<SelectItem key={timekeeper.id} value={timekeeper.id}>
										{formatTimekeeperFullName(timekeeper)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				)}
				<EntryForm
					ref={formRef}
					key={formKey}
					formId="add-entry-form"
					defaultValues={createTimerEntryDefaults({
						date,
						timerDurationSeconds: timerPrefillDuration,
						carryForward: null,
					})}
					autoPopulateLastUsedMatter
					timekeeperId={selectedTimekeeperId}
					onSubmit={(values) =>
						onSubmit(values, canUploadFiles ? files : [], selectedTimekeeperId)
					}
					onExport={(values) =>
						onExport(values, canUploadFiles ? files : [], selectedTimekeeperId)
					}
					onDuplicate={(values) =>
						onDuplicate(
							values,
							canUploadFiles ? files : [],
							selectedTimekeeperId,
						)
					}
				/>
				{canUploadFiles && (
					<div className="px-4 pb-4">
						<AttachmentField files={files} onFilesChange={setFiles} />
					</div>
				)}
			</ResponsiveDialogContent>
		</ResponsiveDialog>
	);
}

interface TimerElapsedBadgeProps {
	durationSeconds: number;
}

function TimerElapsedBadge({ durationSeconds }: TimerElapsedBadgeProps) {
	return (
		<div className="flex h-9 items-center gap-2 rounded-md border border-input bg-background px-3 text-sm font-medium">
			<Timer className="size-3.5 text-muted-foreground" />
			<span className="tabular-nums">
				{formatDurationClock(durationSeconds)}
			</span>
		</div>
	);
}
